import { useState } from 'react';

const AGE_VERIFIED_KEY = 'hs_age_verified';

interface AgeVerificationProps {
  onVerified: () => void;
}

export default function AgeVerification({ onVerified }: AgeVerificationProps) {
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const m = parseInt(month, 10);
    const d = parseInt(day, 10);
    const y = parseInt(year, 10);

    if (
      !month || !day || !year ||
      isNaN(m) || isNaN(d) || isNaN(y) ||
      m < 1 || m > 12 ||
      d < 1 || d > 31 ||
      y < 1900 || y > new Date().getFullYear()
    ) {
      setError('Please enter a valid date of birth.');
      return;
    }

    const birthDate = new Date(y, m - 1, d);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    if (age >= 21) {
      sessionStorage.setItem(AGE_VERIFIED_KEY, 'true');
      onVerified();
    } else {
      setError('You must be 21 years of age or older to access this site.');
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="relative bg-[#0E0E0E] border border-[#B87333]/40 rounded-2xl shadow-2xl w-full max-w-md mx-4 px-8 py-10 flex flex-col items-center">
        {/* Logo / Brand */}
        <div className="mb-6 flex flex-col items-center gap-1">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-fire-fill text-[#B87333] text-3xl"></i>
            </div>
            <span className="font-serif text-2xl font-bold tracking-widest text-white uppercase">High Standards</span>
          </div>
          <span className="text-[#B87333] text-xs tracking-[0.3em] uppercase font-sans">Smokes</span>
        </div>

        <div className="w-12 h-px bg-[#B87333]/50 mb-6"></div>

        <h2 className="font-serif text-2xl font-bold text-white text-center mb-2 leading-snug">
          You must be 21 or older to view this content
        </h2>
        <p className="text-white/50 text-sm text-center mb-8 font-sans">
          Enter your date of birth to confirm
        </p>

        <form onSubmit={handleSubmit} className="w-full">
          <div className="flex gap-3 mb-5">
            {/* Month */}
            <div className="flex-1 flex flex-col items-center gap-1">
              <label className="text-white/60 text-xs uppercase tracking-widest font-sans mb-1">Month</label>
              <input
                type="number"
                min={1}
                max={12}
                placeholder="MM"
                value={month}
                onChange={e => setMonth(e.target.value)}
                className="w-full bg-[#1a1a1a] border border-[#B87333]/30 rounded-lg text-white text-center text-lg font-sans py-3 px-2 focus:outline-none focus:border-[#B87333] transition-colors placeholder-white/20"
              />
            </div>
            {/* Day */}
            <div className="flex-1 flex flex-col items-center gap-1">
              <label className="text-white/60 text-xs uppercase tracking-widest font-sans mb-1">Day</label>
              <input
                type="number"
                min={1}
                max={31}
                placeholder="DD"
                value={day}
                onChange={e => setDay(e.target.value)}
                className="w-full bg-[#1a1a1a] border border-[#B87333]/30 rounded-lg text-white text-center text-lg font-sans py-3 px-2 focus:outline-none focus:border-[#B87333] transition-colors placeholder-white/20"
              />
            </div>
            {/* Year */}
            <div className="flex-1 flex flex-col items-center gap-1">
              <label className="text-white/60 text-xs uppercase tracking-widest font-sans mb-1">Year</label>
              <input
                type="number"
                min={1900}
                max={new Date().getFullYear()}
                placeholder="YYYY"
                value={year}
                onChange={e => setYear(e.target.value)}
                className="w-full bg-[#1a1a1a] border border-[#B87333]/30 rounded-lg text-white text-center text-lg font-sans py-3 px-2 focus:outline-none focus:border-[#B87333] transition-colors placeholder-white/20"
              />
            </div>
          </div>

          {error && (
            <div className="mb-4 text-center text-sm font-sans px-3 py-2 rounded-lg bg-red-900/30 border border-red-500/30 text-red-400">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-[#B87333] hover:bg-[#a0622a] text-white font-sans font-semibold text-base py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap tracking-wide uppercase"
          >
            Confirm
          </button>
        </form>

        <p className="mt-6 text-white/25 text-xs text-center font-sans leading-relaxed">
          By entering, you confirm that you are 21 years of age or older and agree to our terms of use.
        </p>
      </div>
    </div>
  );
}

export { AGE_VERIFIED_KEY };
